import type { Column } from '@tanstack/react-table';
import { endOfDay, isSameDay, startOfDay } from 'date-fns';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';

import { Label } from 'ui/label';
import { DateRangePicker, getPresetRange, type DateRangePreset } from '../DateRangePicker';

type DateSelection = Date[] | DateRange;

function getIsDateRange(value: DateSelection | undefined): value is DateRange {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function parseAsDate(timestamp: number | string | undefined): Date | undefined {
  if (!timestamp) return undefined;
  const numericTimestamp = typeof timestamp === 'string' ? Number(timestamp) : timestamp;
  const date = new Date(numericTimestamp);
  return !Number.isNaN(date.getTime()) ? date : undefined;
}

function parseColumnFilterValue(value: unknown) {
  if (value === null || value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'number' || typeof item === 'string') {
        return item;
      }
      return undefined;
    });
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return [value];
  }

  return [];
}

interface DataTableDateFilterProps<TData> {
  column: Column<TData, unknown>;
  title?: string;
  multiple?: boolean;
  allowFutureDates?: boolean;
  defaultPreset?: DateRangePreset;
  defaultPresetIsFilter?: boolean;
  showDefaultRangeWhenEmpty?: boolean;
}

export function DataTableDateFilter<TData>({
  column,
  title,
  multiple,
  allowFutureDates = false,
  defaultPreset = 'thisMonth',
  defaultPresetIsFilter = true,
  showDefaultRangeWhenEmpty = false,
}: DataTableDateFilterProps<TData>) {
  const columnFilterValue = column.getFilterValue();

  const selectedDates = React.useMemo<DateSelection | undefined>(() => {
    if (!columnFilterValue) {
      return undefined;
    }

    if (multiple) {
      const timestamps = parseColumnFilterValue(columnFilterValue);
      const from = parseAsDate(timestamps[0]);
      const to = parseAsDate(timestamps[1]);
      if (!from && !to) return undefined;
      return { from, to };
    }

    const timestamps = parseColumnFilterValue(columnFilterValue);
    const date = parseAsDate(timestamps[0]);
    return date ? [date] : undefined;
  }, [columnFilterValue, multiple]);

  const onSelect = React.useCallback(
    (date: Date | DateRange | undefined) => {
      if (!date) {
        column.setFilterValue(undefined);
        return;
      }

      if (multiple && !('getTime' in date)) {
        const from = date.from ? startOfDay(date.from).getTime() : undefined;
        const to = date.to ? endOfDay(date.to).getTime() : undefined;

        if (!from && !to) {
          column.setFilterValue(undefined);
          return;
        }

        column.setFilterValue([from, to]);
        return;
      }

      if (!multiple && 'getTime' in date) {
        column.setFilterValue(date.getTime());
      }
    },
    [column, multiple],
  );

  const onPresetSelect = React.useCallback(
    (date: DateRange) => {
      const defaultRange = getPresetRange(defaultPreset, startOfDay(new Date()));
      if (
        !defaultPresetIsFilter &&
        date.from &&
        date.to &&
        defaultRange.from &&
        defaultRange.to &&
        isSameDay(date.from, defaultRange.from) &&
        isSameDay(date.to, defaultRange.to)
      ) {
        column.setFilterValue(undefined);
        return;
      }

      onSelect(date);
    },
    [column, defaultPreset, defaultPresetIsFilter, onSelect],
  );

  return (
    <div className="flex items-center gap-2">
      <Label className="text-caption font-normal">{title}</Label>
      <DateRangePicker
        value={multiple && getIsDateRange(selectedDates) ? selectedDates : undefined}
        onRangeSelect={onSelect}
        onPresetSelect={onPresetSelect}
        allowFutureDates={allowFutureDates}
        defaultPreset={defaultPreset}
        showDefaultRangeWhenEmpty={showDefaultRangeWhenEmpty}
      />
    </div>
  );
}
